import { api } from "../lib/api";
import { sendToTerminal } from "../lib/terminalLogger";
import { TokenService } from "../lib/tokenService";
import {
	Message,
	CreateMessagePayload,
	UpdateMessagePayload,
	MessageFilter,
	PaginatedResponse,
	UnreadCountResponse,
	MessageStats,
	MessageReply,
	CreateReplyPayload,
	ListRepliesParams,
} from "../types/messages";

// Rotas públicas - para todos os usuários autenticados
const PUBLIC_BASE_URL = "/messages";
// Rotas admin - apenas para administradores
const ADMIN_BASE_URL = "/admin/messages";
const LOG_PREFIX = "[MESSAGES_SERVICE]";

/**
 * Normaliza uma mensagem da API garantindo valores padrão para campos obrigatórios
 */
export function normalizeMessage(message: any): Message {
	return {
		...message,
		prioridade: message.prioridade || "normal",
		requer_resposta: message.requer_resposta ?? false,
		resposta_obrigatoria: message.resposta_obrigatoria ?? false,
		resposta_enviada: message.resposta_enviada ?? false,
		resposta_enviada_em: message.resposta_enviada_em || null,
		prazo_resposta: message.prazo_resposta || null,
		is_repetitiva: message.is_repetitiva ?? false,
		horario_exibicao: message.horario_exibicao || null,
		dias_semana: message.dias_semana || null,
		lida: message.lida ?? false,
		lida_em: message.lida_em || null,
		ativa: message.ativa ?? true,
		expira_em: message.expira_em || null,
		destinatarios: message.destinatarios || null,
		roles: message.roles || null,
		criada_por: message.criada_por || null,
		criada_por_nome: message.criada_por_nome || null,
	} as Message;
}

/**
 * Serviço para gerenciar mensagens administrativas
 * Refatorado para remover cache manual (agora usa React Query)
 */
export const messagesService = {
	/**
	 * Lista todas as mensagens com paginação e filtros
	 */
	async getAll(
		params?: MessageFilter,
		useAdminRoute = false,
	): Promise<{ success: true; data: PaginatedResponse<Message> }> {
		const token = TokenService.getAccessToken();
		if (!token) {
			sendToTerminal(
				`🚫 ${LOG_PREFIX} Tentativa de buscar mensagens sem token`,
				{ route: useAdminRoute ? "admin" : "public" },
			);
			throw new Error("Não autenticado - faça login antes de buscar mensagens");
		}
		const queryParams = new URLSearchParams();

		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.tipo) queryParams.append("tipo", params.tipo);
		if (params?.ativa !== undefined)
			queryParams.append("ativa", params.ativa.toString());
		if (params?.lida !== undefined)
			queryParams.append("lida", params.lida.toString());
		if (params?.prioridade) queryParams.append("prioridade", params.prioridade);
		if (params?.requer_resposta !== undefined)
			queryParams.append("requer_resposta", params.requer_resposta.toString());
		if (params?.includeExpired) queryParams.append("includeExpired", "true");
		if (params?.dateFrom) queryParams.append("dateFrom", params.dateFrom);
		if (params?.dateTo) queryParams.append("dateTo", params.dateTo);

		const paramsString = queryParams.toString();
		const baseUrl = useAdminRoute ? ADMIN_BASE_URL : PUBLIC_BASE_URL;
		const url = `${baseUrl}${paramsString ? `?${paramsString}` : ""}`;

		try {
			const response = await api(url, { method: "GET" });

			// Normalizar mensagens
			if (response.data?.messages) {
				response.data.messages = response.data.messages.map(normalizeMessage);
			}

			return response;
		} catch (error: any) {
			const errorDetails: any = {
				endpoint: url,
				route: useAdminRoute ? "admin" : "public",
				status: error?.status,
			};

			if (error?.status === 404) {
				sendToTerminal(`⚠️ ${LOG_PREFIX} Rota não encontrada`, errorDetails);
				return {
					success: true as const,
					data: {
						messages: [] as Message[],
						pagination: { page: 1, limit: 20, total: 0, pages: 0 },
					},
				};
			}

			if (error?.status === 403) throw new Error("Acesso negado");
			if (error?.status === 401) throw new Error("Não autenticado");

			sendToTerminal(`❌ ${LOG_PREFIX} Erro ao buscar mensagens`, {
				...errorDetails,
				error: error?.message || "Erro desconhecido",
			});
			throw error;
		}
	},

	/**
	 * Retorna a quantidade de mensagens não lidas
	 */
	async getUnreadCount(): Promise<{
		success: true;
		data: UnreadCountResponse;
	}> {
		const token = TokenService.getAccessToken();
		if (!token) {
			return { success: true as const, data: { unread_count: 0 } };
		}

		try {
			// Usando api() diretamente para aproveitar interceptors
			const response = await api(`${PUBLIC_BASE_URL}/unread-count`, {
				// Usando api() ao invés de fetch manual da refatoração anterior?
				// Ah, o código original usava api(). Mantemos api().
				method: "GET",
			});
			return response;
		} catch (error: any) {
			if (error?.status === 404) {
				return { success: true as const, data: { unread_count: 0 } };
			}
			throw error;
		}
	},

	/**
	 * Busca uma mensagem específica por ID
	 */
	async getById(messageId: number): Promise<{ success: true; data: Message }> {
		const response = await api(`${PUBLIC_BASE_URL}/${messageId}`, {
			method: "GET",
		});

		if (response.data) {
			response.data = normalizeMessage(response.data);
		}

		return response;
	},

	/**
	 * Cria uma nova mensagem
	 */
	async create(
		data: CreateMessagePayload,
	): Promise<{ success: true; data: Message; message: string }> {
		sendToTerminal(`📤 ${LOG_PREFIX} Criando mensagem (admin)`, {
			titulo: data.titulo,
		});

		try {
			const response = await api(ADMIN_BASE_URL, {
				method: "POST",
				body: JSON.stringify(data),
			});

			if (response.data) {
				response.data = normalizeMessage(response.data);
			}

			return response;
		} catch (error: any) {
			const errorMsg = error?.message || "Erro ao criar mensagem";
			throw new Error(errorMsg);
		}
	},

	/**
	 * Atualiza uma mensagem existente
	 */
	async update(
		messageId: number,
		data: UpdateMessagePayload,
	): Promise<{ success: true; data: Message; message: string }> {
		sendToTerminal(`📤 ${LOG_PREFIX} Atualizando mensagem (admin)`, {
			messageId,
		});

		const response = await api(`${ADMIN_BASE_URL}/${messageId}`, {
			method: "PUT",
			body: JSON.stringify(data),
		});

		if (response.data) {
			response.data = normalizeMessage(response.data);
		}

		return response;
	},

	/**
	 * Marca uma mensagem como lida
	 */
	async markAsRead(messageId: number): Promise<{
		success: true;
		data: { id: number; lida: true; lida_em: string; unread_count: number };
		message: string;
	}> {
		return api(`${PUBLIC_BASE_URL}/${messageId}/read`, {
			method: "PATCH",
		});
	},

	/**
	 * Marca múltiplas mensagens como lidas em uma única requisição (batch)
	 */
	async markMultipleAsRead(messageIds: number[]): Promise<{
		success: true;
		data: {
			marked: number[];
			unread_count: number;
		};
		message: string;
	}> {
		return api(`${PUBLIC_BASE_URL}/read`, {
			method: "PATCH",
			body: JSON.stringify({ message_ids: messageIds }),
		});
	},

	/**
	 * Exclui uma mensagem permanentemente
	 */
	async delete(messageId: number): Promise<{ success: true; message: string }> {
		return api(`${ADMIN_BASE_URL}/${messageId}`, { method: "DELETE" });
	},

	/**
	 * Obtém estatísticas de leitura de uma mensagem (admin only)
	 */
	async getStats(
		messageId: number,
	): Promise<{ success: true; data: MessageStats }> {
		return await api(`${ADMIN_BASE_URL}/${messageId}/stats`, {
			method: "GET",
		});
	},

	/**
	 * Envia uma resposta para uma mensagem
	 */
	async sendReply(
		messageId: number,
		data: CreateReplyPayload,
	): Promise<{ success: true; data: MessageReply; message: string }> {
		return await api(`${PUBLIC_BASE_URL}/${messageId}/reply`, {
			method: "POST",
			body: JSON.stringify(data),
		});
	},

	/**
	 * Lista respostas de uma mensagem
	 */
	async getReplies(
		messageId: number,
		params?: ListRepliesParams,
	): Promise<{
		success: true;
		data: { replies: MessageReply[]; pagination?: any };
	}> {
		const queryParams = new URLSearchParams();

		if (params?.page) queryParams.append("page", params.page.toString());
		if (params?.limit) queryParams.append("limit", params.limit.toString());
		if (params?.user_id)
			queryParams.append("user_id", params.user_id.toString());

		const url = `${PUBLIC_BASE_URL}/${messageId}/replies${
			queryParams.toString() ? `?${queryParams.toString()}` : ""
		}`;

		return await api(url, { method: "GET" });
	},

	// Método mantido para compatibilidade, mas agora vazio pois não há cache manual
	clearCache() {
		// No-op
	},
};
